// Sides, for the 2 mot 2. Which modes exist and which of them has sides lives in
// modes.js; this is only the membership and the "has a side lost" rule.
//
// Nothing here imports player.js: the liveness test arrives as an argument instead,
// because player.js needs normalizeTeam and a cycle between the two would be worse
// than one extra parameter.
import {isTeamMode, seatsFor, TEAM_MODE} from "./modes.js";

export const TEAM_COUNT = 2;
export const TEAM_ROSTER = seatsFor(TEAM_MODE);
export const TEAM_LABELS = ["Lag 1", "Lag 2"];
// What the last player on a side draws at the start of their turn. Two against one is
// otherwise unwinnable: the intact side plays two cards a round to your one and simply
// out-draws you. Handing the survivor the cards their partner is no longer drawing
// keeps the match a match — it does not make them stronger than the pair they face.
export const SOLO_SURVIVOR_DRAWS = 2;

export {TEAM_MODE};

export function normalizeTeam(value) {
  return value === 1 ? 1 : 0;
}

// Seats alternate, so the turn order runs 1, 2, 1, 2. Sitting a team's two phones
// back to back would let one side take two turns in a row, which reads as two solo
// games rather than a duel.
export function nextTeam(players = []) {
  return players.length % TEAM_COUNT;
}

export function teamLabel(team) {
  return TEAM_LABELS[normalizeTeam(team)];
}

export function membersOf(players = [], team) {
  const side = normalizeTeam(team);
  return players.filter((player) => normalizeTeam(player.team) === side);
}

export function teammatesOf(players = [], player) {
  return membersOf(players, player?.team).filter((mate) => mate.id !== player?.id);
}

export function opponentsOf(players = [], player) {
  return players.filter((other) => normalizeTeam(other.team) !== normalizeTeam(player?.team));
}

// A side is beaten only once every one of its seats is out, so the last one standing
// plays on two against one instead of being retired alongside their partner. A team
// nobody is left in — both walked out — counts as beaten for the same reason.
export function teamMatchOutcome({players = []} = {}, isDead) {
  if (!Array.isArray(players) || players.length === 0) return {ended: false, team: null, members: []};

  const sides = [0, 1].map((team) => {
    const members = membersOf(players, team);
    return {team, members, living: members.filter((player) => !isDead(player))};
  });

  const standing = sides.filter((side) => side.living.length);
  if (standing.length > 1) return {ended: false, team: null, members: []};

  // Both sides down in the same blow is rare but possible — a reflected hit can take
  // the last two out at once. Least drunk takes it rather than nobody winning. Only
  // sides that still have someone in them can win: with everyone from one side gone
  // the empty side would otherwise "win" on zero sips with nobody to celebrate, and
  // the roster is never empty here because that returned above.
  const contenders = sides.filter((side) => side.members.length);
  const won = standing[0] || [...contenders].sort((a, b) => sipsOf(a) - sipsOf(b))[0];
  return {ended: true, team: won.team, members: won.members};
}

function sipsOf(side) {
  return side.members.reduce((total, player) => total + (player.sipsTaken || 0), 0);
}

// How many cards this player draws at the start of their turn. One for everybody,
// except the last one standing on a side of a 2 mot 2. A partner who left the lobby
// counts the same as one who is out — either way you are alone over there.
export function turnDraws(state, player, isDead) {
  if (!isTeamMode(state)) return 1;
  const mates = teammatesOf(state?.players || [], player);
  return mates.some((mate) => !isDead(mate)) ? 1 : SOLO_SURVIVOR_DRAWS;
}
